import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import {
  type AccountOthersData,
  GET_ACCOUNT_OTHERS
} from '../queries/accountOthersQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountOthersData;
  isDate?: boolean;
}

const CONTACT_PREFERENCES_FIELDS: FieldMapping[] = [
  { apiField: 'preferredEmailAddress', formField: 'preferredEmailAddress' },
  {
    apiField: 'preferredMethodOfContact',
    formField: 'preferredMethodOfContact'
  },
  { apiField: 'followEmailActivity', formField: 'followEmailActivity' },
  { apiField: 'gdprOptout', formField: 'gdprOptout' },
  {
    apiField: 'reverseSolicitationEmailReceivedDate',
    formField: 'reverseSolicitationEmailReceivedDate',
    isDate: true
  }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountOthersData,
  initial: AccountOthersData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else if (typeof currentVal === 'boolean') {
        changes[apiField] = currentVal;
      } else {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        preferredEmailAddress
        preferredMethodOfContact
        followEmailActivity
        gdprOptout
        reverseSolicitationEmailReceivedDate
      }
    }
  }
`;

export interface EditContactPreferencesDialogProps {
  accountId: string;
  initialValue: AccountOthersData;
  isOpen: boolean;
  onChange: (data: AccountOthersData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountOthersData;
}

export function EditContactPreferencesDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditContactPreferencesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_OTHERS, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(
    CONTACT_PREFERENCES_FIELDS,
    value,
    initialValue
  );
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountOthersData>(
    field: K,
    fieldValue: AccountOthersData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save contact preferences');
      } else {
        toast.success('Contact preferences saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving contact preferences:', error);
      toast.error('Failed to save contact preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Contact Preferences"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="Preferred Email Address"
          onChange={preferredEmailAddress => {
            updateField('preferredEmailAddress', preferredEmailAddress);
          }}
          type="email"
          value={value.preferredEmailAddress}
        />
        <EditDynamicSelectField
          fieldName="preferred_method_of_contact"
          label="Preferred Method of Contact"
          model="opstream.account"
          onChange={v => {
            updateField('preferredMethodOfContact', v);
          }}
          value={value.preferredMethodOfContact}
        />
        <EditCheckboxField
          checked={value.followEmailActivity}
          label="Follow Email Activity"
          onCheckedChange={followEmailActivity => {
            updateField('followEmailActivity', followEmailActivity);
          }}
        />
        <EditCheckboxField
          checked={value.gdprOptout}
          label="GDPR Optout"
          onCheckedChange={gdprOptout => {
            updateField('gdprOptout', gdprOptout);
          }}
        />
        <EditFormField
          label="Reverse Solicitation Email Received Date"
          onChange={reverseSolicitationEmailReceivedDate => {
            updateField(
              'reverseSolicitationEmailReceivedDate',
              reverseSolicitationEmailReceivedDate
            );
          }}
          type="date"
          value={toDateInputValue(value.reverseSolicitationEmailReceivedDate)}
        />
      </div>
    </EditDialog>
  );
}
